import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { format } from 'date-fns';
import JSZip from 'jszip';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createContainer } from 'unstated-next';
import * as yup from 'yup';

import { reportProblemRequest, Parameters } from './requests/report_problem';
import { ProblemType } from './types';
import { useAppMessaging } from '../../hooks/useAppMessaging';
import { useDeploymentInfo } from '../../hooks/useDeploymentInfo';
import { useUserAgent } from '../../hooks/useUserAgent';
import { getEnvironment } from '../../utils/environment';
import { getUrlParams } from '../../utils/url';

const MAXIMUM_SIZE_LIMIT_MB = 20;

const useReportProblemContainer = () => {
    const deploymentInfo = useDeploymentInfo();
    const { t } = useTranslation('report_problem');
    const { closeWindow, sendMessage } = useAppMessaging();

    const { account } = getEnvironment();

    const {
        token,
        class_id: classId,
        session_id: sessionId,
        username,
        email,
        app_version: cfzAppVersion = `Unknown`,
        sentry_id: sentryId,
        // The lock_problem_type parameter allows you to lock the problem type field to a specific problem type.
        // An example of this being used can be found in the recording player's implementation of this popup where it
        // applies a lock_problem_type of 'recording_playback'.
        lock_problem_type: lockProblemType
    } = getUrlParams();

    const today = new Date();

    const schema = yup.object({
        problemType: yup.string().required(t('errors.problem-type')),
        description: yup.string().required(t('errors.description')),
        organization: yup.string().optional(),
        occurence: yup.date().max(today, t('errors.date')),
        attachments: yup
            .array<File>()
            .optional()
            .test('attachmentsTest', (files: File[] | undefined, { createError }) => {
                if (!files) {
                    return true;
                }

                const totalSizeMB = files.reduce((acc, { size }) => acc + size, 0) / 1000000;
                if (totalSizeMB > MAXIMUM_SIZE_LIMIT_MB) {
                    return createError({
                        message: t('errors.attachment-file-size', { fileSize: MAXIMUM_SIZE_LIMIT_MB }),
                        path: 'attachments'
                    });
                }

                return true;
            }),
        contactName: yup.string().required(t('errors.name')),
        contactEmail: yup.string().email(t('errors.email-format')).required(t('errors.email'))
    });

    const defaultValues = {
        problemType: (lockProblemType as ProblemType | undefined) ?? ProblemType.Unspecified,
        description: '',
        organization: '',
        occurence: format(today, "yyyy-MM-dd'T'HH:mm"),
        attachments: [] as File[],
        contactName: username || '',
        contactEmail: email || ''
    };

    const { control, formState, watch, handleSubmit, register, getValues, reset, setValue, resetField } = useForm({
        mode: 'onChange',
        defaultValues,
        resolver: yupResolver(schema)
    });

    const { browser, os } = useUserAgent(navigator.userAgent);

    const returnPayloadFromFormValues = () => getValues();

    const createZipFile = async (summary, attachments?: File[]): Promise<Blob> => {
        const zip = new JSZip();
        zip.file('summary.json', JSON.stringify(summary, null, 2));

        attachments?.forEach((attachment) => {
            zip.file(`attachments/${attachment.name}`, attachment);
        });

        return zip.generateAsync({ type: 'blob' });
    };

    const reportProblem = async () => {
        const { problemType, attachments, description, occurence, contactName, contactEmail } =
            returnPayloadFromFormValues();

        // Remove T from timestamp
        const occurenceTime = occurence.replace('T', ' ');

        const descriptionArray = [
            'Description:',
            description,
            '',
            '',
            'Details',
            `Time: ${occurenceTime}`,
            `Name: ${contactName}`,
            `Email: ${contactEmail}`,
            `Account: ${account}`
        ];

        const body: Parameters = {
            problem_type: problemType,
            description: descriptionArray.join('\n'),
            contact: contactEmail,
            class_id: classId,
            session_id: sessionId
        };

        const response = await reportProblemRequest(token, cfzAppVersion, body);

        const summary = {
            category: problemType,
            contact_name: contactName,
            email: contactEmail,
            time_of_occurence: occurence,
            device_info: {
                version: cfzAppVersion,
                os,
                browser
            },
            deployment_info: deploymentInfo,
            description,
            sentry_id: sentryId || 'No sentry ID provided',
            sentry_event_url: sentryId
                ? `https://classedu.sentry.io/issues/?query=id:${sentryId}`
                : 'No sentry event URL'
        };

        const s3File = await createZipFile(summary, attachments);

        const attachmentResponse = await axios.put(response.data.url, s3File, {
            headers: {
                'Content-Type': s3File.type
            }
        });

        if (attachmentResponse.status !== 200) {
            throw new Error(JSON.stringify(attachmentResponse.data));
        }
    };

    const onCancel = () => {
        // This component is opened from class.js in new window, so close it
        window.close();

        // If opened in dialog (CfT, Nextgen), send message to parent window to handle closing of dialog
        closeWindow();
    };

    const handleSuccess = () => {
        sendMessage({ func: 'success' });

        setTimeout(onCancel, 3000);
    };

    return {
        control,
        formState,
        watch,
        handleSubmit,
        register,
        getValues,
        reset,
        setValue,
        resetField,
        reportProblem,
        lockProblemType,
        onCancel,
        handleSuccess
    };
};

const ReportProblemContainer = createContainer(useReportProblemContainer);

export default ReportProblemContainer;
